import React, { useState, useRef, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface FAQItemData {
  questionKey: string;
  answerKey: string;
}
const faqItems: FAQItemData[] = [{
  questionKey: 'faq.q1',
  answerKey: 'faq.a1'
}, {
  questionKey: 'faq.q2',
  answerKey: 'faq.a2'
}, {
  questionKey: 'faq.q3',
  answerKey: 'faq.a3'
}, {
  questionKey: 'faq.q4',
  answerKey: 'faq.a4'
}, {
  questionKey: 'faq.q5',
  answerKey: 'faq.a5'
}, {
  questionKey: 'faq.q6',
  answerKey: 'faq.a6'
}];
function FAQItemComponent({
  item,
  index
}: {
  item: FAQItemData;
  index: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const {
    t
  } = useLanguage();
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 24
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5,
    delay: index * 0.07,
    ease: [0.16, 1, 0.3, 1]
  }} className="border-b border-gray-200 last:border-none" data-id="element-1325">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="w-full flex items-center justify-between gap-4 py-6 text-left group" data-id="element-1326">
        <span className="font-nunito font-bold text-base sm:text-lg text-haa-navy group-hover:text-haa-blue transition-colors duration-200 leading-snug" data-id="element-1327">
          {t(item.questionKey)}
        </span>
        <motion.div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200 ${open ? 'bg-haa-blue text-white' : 'bg-gray-100 text-haa-navy group-hover:bg-haa-blue/10 group-hover:text-haa-blue'}`} data-id="element-1328">
          {open ? <MinusIcon className="w-4 h-4" data-id="element-1329" /> : <PlusIcon className="w-4 h-4" data-id="element-1330" />}
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-1331">
        {open && <motion.div key="answer" initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }} className="overflow-hidden" data-id="element-1332">
            <p className="font-nunito text-gray-600 text-base leading-relaxed pb-6 pr-12" data-id="element-1333">
              {t(item.answerKey)}
            </p>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQ() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    t
  } = useLanguage();
  return <>
      {/* Blue divider above FAQ */}
      <div className="h-2 w-full flex" data-id="element-1334">
        <div className="flex-1 bg-haa-blue" data-id="element-1335" />
        <div className="flex-1 bg-white" data-id="element-1336" />
        <div className="flex-1 bg-haa-blue" data-id="element-1337" />
      </div>

      <section id="faq" className="bg-gray-50 py-20 lg:py-28" aria-labelledby="faq-heading" data-id="element-1338">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1339">
          <div ref={titleRef} className="text-center mb-14" data-id="element-1340">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={titleInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} data-id="element-1341">
              <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-1342">
                {t('faq.label')}
              </span>
              <h2 id="faq-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-1343">
                {t('faq.heading')}
              </h2>
              <div className="flex items-center justify-center gap-3 mt-4" data-id="element-1344">
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-1345" />
                <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-1346" />
                <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-1347" />
              </div>
              <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-1348">
                {t('faq.description')}
              </p>
            </motion.div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="bg-white rounded-3xl shadow-lg px-6 sm:px-10 divide-y divide-gray-200" data-id="element-1349">
            {faqItems.map((item, i) => <FAQItemComponent key={i} item={item} index={i} data-id="element-1350" />)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          delay: 0.5,
          duration: 0.5
        }} className="mt-12 text-center" data-id="element-1351">
            <p className="font-nunito text-gray-500 text-base mb-4" data-id="element-1352">
              {t('faq.contact.text')}
            </p>
            <motion.a href="mailto:info@honduranamerican.org" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2 bg-gradient-to-r from-haa-blue to-haa-midblue text-white font-nunito font-bold text-base px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-shadow" data-id="element-1353">
              {t('faq.contact.button')}
            </motion.a>
          </motion.div>
        </div>
      </section>
    </>;
}